import * as Dialog from '@radix-ui/react-dialog'
import { useEffect, useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { useToast } from '@/components/ui/Toast'
import { IconButton } from '@/components/ui/IconButton'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Avatar } from '@/components/ui/Avatar'
import { AvatarColorPicker } from '@/components/ui/AvatarColorPicker'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { ChangePasswordForm } from './ChangePasswordForm'
import { updateProfile } from '@/api/endpoints/auth'
import { ApiError } from '@/api/errors'
import { defaultAvatarColor, isAvatarColorName, type AvatarColorName } from '@/lib/avatarColors'
import { LogOut, User, ShieldCheck, X } from '@/components/icons'
import { cn } from '@/lib/cn'

type Section = 'profile' | 'security'

/**
 * Unified account settings dialog (avatar, name, password, logout).
 */
export function ProfileDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { user, signOut } = useAuth()
  const [section, setSection] = useState<Section>('profile')
  const [dirty, setDirty] = useState(false)
  const [confirmDiscard, setConfirmDiscard] = useState(false)

  // Reopening always starts on Profile, with a clean dirty flag.
  useEffect(() => {
    if (open) {
      setSection('profile')
      setDirty(false)
    }
  }, [open])

  if (!user) return null

  function requestClose() {
    if (dirty) setConfirmDiscard(true)
    else onClose()
  }

  const avatarColor: AvatarColorName =
    user.avatar_color && isAvatarColorName(user.avatar_color)
      ? user.avatar_color
      : defaultAvatarColor(user.id)

  return (
    <>
      <Dialog.Root open={open} onOpenChange={(next) => { if (!next) requestClose() }}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-black/30" />
          <Dialog.Content
            className={cn(
              'fixed top-1/2 left-1/2 z-50 flex h-[min(560px,85vh)] w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2',
              'overflow-hidden rounded-lg border border-line bg-surface shadow-overlay focus:outline-none',
              'md:w-[640px]',
            )}
          >
            {/* Radix requires a Title even when we render our own header inside
                the content column — visually hidden here, read by screen readers. */}
            <Dialog.Title className="sr-only-focusable">Your account</Dialog.Title>

            <nav
              aria-label="Account sections"
              className="flex w-40 shrink-0 flex-col border-r border-line bg-sunken md:w-48"
            >
              <div className="flex flex-col items-center gap-1 border-b border-line px-3 py-4 text-center">
                <Avatar name={user.name} size="lg" color={avatarColor} />
                <p className="mt-1 truncate text-sm font-medium text-text">{user.name}</p>
                <p className="truncate text-xs text-subtle">{user.role.name}</p>
              </div>

              <ul className="flex flex-1 flex-col gap-0.5 p-2">
                <RailItem
                  icon={User}
                  label="Profile"
                  active={section === 'profile'}
                  onClick={() => { setSection('profile') }}
                />
                <RailItem
                  icon={ShieldCheck}
                  label="Security"
                  active={section === 'security'}
                  onClick={() => { setSection('security') }}
                />
              </ul>

              <div className="border-t border-line p-2">
                <button
                  type="button"
                  onClick={() => { void signOut() }}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-sm text-muted',
                    'transition-colors duration-150 hover:bg-hover hover:text-text',
                    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
                  )}
                >
                  <LogOut size={16} strokeWidth={1.75} aria-hidden />
                  Sign out
                </button>
              </div>
            </nav>

            <div className="flex flex-1 flex-col overflow-hidden">
              <header className="flex shrink-0 items-center justify-between border-b border-line px-4 py-3">
                <h2 className="text-h3 font-semibold text-text">
                  {section === 'profile' ? 'Profile' : 'Security'}
                </h2>
                <Dialog.Close asChild>
                  <IconButton icon={X} label="Close" size="sm" onClick={requestClose} />
                </Dialog.Close>
              </header>

              <div className="flex-1 overflow-y-auto px-4 py-4">
                {section === 'profile' ? (
                  <ProfileSection color={avatarColor} onDirtyChange={setDirty} />
                ) : (
                  <SecuritySection />
                )}
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <ConfirmDialog
        open={confirmDiscard}
        onCancel={() => { setConfirmDiscard(false) }}
        onConfirm={() => { setConfirmDiscard(false); setDirty(false); onClose() }}
        title="Discard changes?"
        body="You have unsaved changes to your name or avatar. Closing now will lose them."
        confirmLabel="Discard"
        tone="danger"
      />
    </>
  )
}

function RailItem({
  icon: Icon,
  label,
  active,
  onClick,
}: {
  icon: typeof User
  label: string
  active: boolean
  onClick: () => void
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        aria-current={active ? 'page' : undefined}
        className={cn(
          'flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-sm',
          'transition-colors duration-150',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
          active ? 'bg-selected font-medium text-text' : 'text-muted hover:bg-hover hover:text-text',
        )}
      >
        <Icon size={16} strokeWidth={1.75} aria-hidden />
        {label}
      </button>
    </li>
  )
}

function ProfileSection({
  color,
  onDirtyChange,
}: {
  color: AvatarColorName
  onDirtyChange: (dirty: boolean) => void
}) {
  const { user, refresh } = useAuth()
  const { toast } = useToast()

  const [name, setName] = useState(user?.name ?? '')
  const [pickedColor, setPickedColor] = useState<AvatarColorName>(color)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const dirty = name.trim() !== (user?.name ?? '') || pickedColor !== color

  useEffect(() => { onDirtyChange(dirty) }, [dirty, onDirtyChange])
  // Clear the dirty flag on unmount, so switching to Security and back does
  // not carry a stale "unsaved changes" warning into a fresh mount.
  useEffect(() => () => { onDirtyChange(false) }, [onDirtyChange])

  async function save() {
    setSaving(true)
    setError(null)
    try {
      await updateProfile({ name: name.trim(), avatar_color: pickedColor })
      await refresh()
      toast('Profile updated.')
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.status === 404
            // Honest, not a generic failure — this endpoint is not live yet.
            ? "Saving isn't connected on the server yet. Nothing was lost — try again once it ships."
            : err.message
          : 'Could not save your profile.',
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex flex-col gap-5">
      {error && (
        <p role="alert" className="rounded-md border border-danger-border bg-danger-bg px-3 py-2 text-sm text-danger">
          {error}
        </p>
      )}

      <div className="flex flex-col gap-2">
        <span className="text-xs font-medium text-muted">Avatar</span>
        <div className="flex items-center gap-3">
          <Avatar name={name || user?.name || '?'} size="lg" color={pickedColor} />
          <AvatarColorPicker value={pickedColor} onChange={setPickedColor} disabled={saving} />
        </div>
      </div>

      <Input
        label="Full name"
        required
        value={name}
        onChange={(e) => { setName(e.target.value) }}
        disabled={saving}
      />

      <div className="flex flex-col gap-1">
        <span className="text-xs font-medium text-muted">Email address</span>
        <span className="text-sm text-subtle">{user?.email}</span>
        <span className="text-xs text-subtle">Contact a Super Admin to change your email.</span>
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-xs font-medium text-muted">Role</span>
        <span className="text-sm text-subtle">{user?.role.name} — set by an administrator</span>
      </div>

      <div className="flex justify-end">
        <Button
          variant="primary"
          loading={saving}
          disabled={!dirty || name.trim().length === 0}
          onClick={() => { void save() }}
        >
          Save changes
        </Button>
      </div>
    </div>
  )
}

function SecuritySection() {
  const { toast } = useToast()
  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-muted">Change your password.</p>
      <ChangePasswordForm onSuccess={() => { toast('Password changed. Your other sessions have been signed out.') }} />
    </div>
  )
}
